<template>
  <Modal
    v-if="currentEditTheme"
    :use-default-body="false"
    :use-cross-icon="true"
    :title="t('news_page_themes_edit_popup.title')"
    teleport="#app"
    width="588px"
    height="735px"
    @close="checkConfirm"
  >
    <div class="themes-edit-popup">
      <InputPrimary
        ref="inputNameRef"
        v-model.trim="currentEditTheme.name"
        :required="true"
        :placeholder="t('news_page_themes_edit_popup.placeholder.theme_name')"
        :label="t('news_page_themes_edit_popup.label.theme_name')"
      />

      <div class="themes-edit-popup__selected-rubrics">
        <div class="themes-edit-popup__selected-rubrics-label themes-edit-popup__selected-rubrics-label--required">
          {{ t('news_page_themes_edit_popup.label.rubric') }}
        </div>

        <div
          v-if="selectedRubrics.length > 0"
          class="themes-edit-popup__selected-rubrics-list scroll"
        >
          <Chip
            v-for="item in selectedRubrics"
            :key="item.id"
            :label="item.name"
            :use-location-icon="false"
            @cancel="toggleTheme(item)"
          />
        </div>
      </div>

      <div class="themes-edit-popup__rubrics">
        <div class="themes-edit-popup__rubrics-search">
          <InputSearchSecondary
            v-model="searchString"
            :placeholder="t('news_page_themes_edit_popup.placeholder.search')"
          />
        </div>
        <div class="themes-edit-popup__rubrics-list">
          <div class="themes-edit-popup__rubrics-list--scroll scroll">
            <div
              v-for="item in allRubrics"
              :key="item"
              class="themes-edit-popup__rubrics-item"
              :class="{'themes-edit-popup__rubrics-item--active': currentEditTheme.rubric_ids.includes(item.id)}"
              @click="toggleTheme(item)"
            >
              <Checkbox
                :readonly="true"
                :model-value="currentEditTheme.rubric_ids.includes(item.id)"
                :label="item.name"
                @click.stop
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <NewsPagePopupTelegramBlock />

    <template #footer>
      <ButtonAccentPrimary
        :disabled="isSaveButtonDisabled"
        :pressed="isSaveButtonClicked"
        size="large"
        @click="saveChanges"
      >
        {{ t('news_page_themes_edit_popup.button.save') }}
      </ButtonAccentPrimary>
      <ButtonNeutralSecondary
        v-if="!props.isNewTheme"
        size="large"
        @click="emit('open-delete-popup')"
      >
        {{ t('news_page_popup_delete.button.accept_delete') }}
      </ButtonNeutralSecondary>
    </template>
  </Modal>

  <!-- Окно подтверждения изменений -->
  <Modal
    v-if="isShowConfirmModal"
    :title="t('news_page_popup_delete.title.unsaved_changes')"
    :confirm-text="t('news_page_popup_delete.button.accept_unsaved')"
    :cancel-text="t('news_page_popup_delete.button.cancel_button')"
    teleport="#app"
    @cancel="isShowConfirmModal = false"
    @confirm="emit('close-edit-popup')"
  >
    {{ t('news_page_popup_delete.message.unsaved_changes') }}
  </Modal>
</template>
<script setup>
import {defineProps, defineEmits, computed, ref, onMounted, nextTick} from "vue";
import { useRubricsStore } from "@/store/NewsFeed/rubrics";
import { t } from "@/controllerLK/GlobusLocalization";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import { useBlockStore } from "@/store/NewsFeed/blocks";
import NewsPagePopupTelegramBlock from "@/components/NewsPage/NewsPagePopup/NewsPagePopupTelegramBlock.vue";

const props = defineProps({
  theme: {
    type: [Object, null],
    required: true
  },
  isNewTheme: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(["close-edit-popup", "open-delete-popup"]);

const blockStore = useBlockStore();
const rubricsStore = useRubricsStore();
const newsFeedStore = useNewsFeedStore();

onMounted(async () => {
  currentEditTheme.value = JSON.parse(JSON.stringify(props.theme));

  defaultTheme = JSON.parse(JSON.stringify(props.theme));

  // Устанавливаем фокус на поле ввода названия, после загрузки темы.
  await nextTick();

  if (currentEditTheme.value.name.length === 0 && inputNameRef.value) {
    inputNameRef.value.focus();
  }
});

// Реф на поле ввода названия темы
const inputNameRef = ref(null);

// Текущая редактируемая тема.
const currentEditTheme = ref(null);
// Дефолтное состояние темы.
let defaultTheme = null;

// Флаг. Отображение окна подтверждения изменений.
const isShowConfirmModal = ref(false);

// Флаг. Клик на кнопку сохранения изменеий.
const isSaveButtonClicked = ref(false);

// Флаг. Активна ли кнопка для сохранения изменений
const isSaveButtonDisabled = computed(
    () => currentEditTheme.value.name.length === 0 || currentEditTheme.value.rubric_ids.length === 0);

// Значение поисковой строки тем.
const searchString = ref("");

// Список включенных рубрик в тему.
const selectedRubrics = computed(
    () => rubricsStore.allRubrics.filter((rubric) => currentEditTheme.value.rubric_ids.includes(rubric.id)));

// Список всех рубрик со значением поисковой строки
const allRubrics = computed(() => {
  if (searchString.value.length === 0) {
    return rubricsStore.allRubrics.filter((rubric) => rubric.is_fixed);
  } else {
    return rubricsStore.allRubrics
        .filter((rubric) => rubric.is_fixed && rubric.name.toLowerCase().includes(searchString.value.toLowerCase()));
  }
});

// Функция включения/Выключения рубрики в теме.
const toggleTheme = (item) => {
  if (currentEditTheme.value.rubric_ids.includes(item.id)) {
    currentEditTheme.value.rubric_ids = currentEditTheme.value.rubric_ids.filter((rubric) => rubric !== item.id);
  } else {
    currentEditTheme.value.rubric_ids.push(item.id);
  }
};

// Функция проверки наличия изменений.
function checkConfirm() {
  if (JSON.stringify(defaultTheme) !== JSON.stringify(currentEditTheme.value)) {
    isShowConfirmModal.value = true;
  } else {
    emit("close-edit-popup");
  }
}

// Функция сохранения изменений/создания рубрики.
async function saveChanges() {
  isSaveButtonClicked.value = true;
  if (currentEditTheme.value.name) {
    if (!props.isNewTheme) {
      await blockStore.updateTheme(currentEditTheme.value);
      emit("close-edit-popup");
      await blockStore.getAllThemes();
      await rubricsStore.getAllRubrics();
      await newsFeedStore.clearNewsFeed();
      await newsFeedStore.getNews();
    } else {
      await blockStore.addNewTheme(currentEditTheme.value);
      emit("close-edit-popup");
    }
  }
  isSaveButtonClicked.value = false;
}
</script>
<style lang="scss" scoped>
.themes-edit-popup {
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  min-height: 0;

  &__selected-rubrics {
    display: flex;
    flex-direction: column;
    gap: 5px;
    max-height: 250px;

    &-label {
      font-weight: 500;
      font-size: 14px;
      line-height: 19.6px;

      &--required:after {
        content: " *";
        color: red;
      }
    }

    &-list {
      display: flex;
      flex-wrap: wrap;
      gap: 5px;
      padding-right: 2px;
    }
  }

  &__rubrics {
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
    min-height: 0;

    &-list {
      flex: 1;
      border: 1px solid #DEDEDE;
      border-radius: 8px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 10px;
      min-height: 0;
      overflow: hidden;

      &--scroll {
        flex: 1;
        padding-right: 2px;
      }
    }

    &-scroll {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    &-item {
      cursor: pointer;
      display: flex;
      align-items: center;
      border-radius: 8px;
      padding: 6px 14px;
      gap: 10px;

      &:hover {
        background: #EEF1F4;

        &:deep(input) {
          border-color: var(--icon-icon-on);
        }
      }

      &--active {
        color: #1F9AFF;

        &:hover {
          background: #e0f0ff;
        }
      }
    }
  }
}

</style>